import { Component, OnInit } from '@angular/core';
import {
  debounceTime,
  distinctUntilChanged,
  lastValueFrom,
  Subject,
  tap,
} from 'rxjs';
import { ApiCore } from 'src/app/config/api';
import { ApiService } from 'src/app/core/http/api.service';
import { Pagination } from 'src/app/shared/components/table-pagination/interfaces/pagination.interface';
import { QueryParamsTable } from 'src/app/shared/components/table-pagination/interfaces/query-params.interface';
import { UserTransactions } from '../transaction-history/interfaces/transactions.interface';
import { Game, IGamesPlayed } from './interfaces/games-played.interface';
import { ActivatedRoute, Router } from '@angular/router';
import { PaidSupplierService } from 'src/app/components/paid-supplier-verification/paid-supplier.service';

@Component({
  selector: 'app-last-bets',
  templateUrl: './last-bets.component.html',
  styleUrls: ['./last-bets.component.css'],
})
export class LastBetsComponent implements OnInit {
  public globalFilter: string = '';
  public loading = true;
  public debounceSearch = new Subject<string>();
  public paramsFilter = {
    search: '',
  };
  public pages: (number | string)[] = [];
  public showing: number = 0;
  public from: number = 0;
  public to: number = 0;
  public total: number = 0;
  public defaultPaginationData = {
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    pageSize: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  };
  public pagination: Pagination;
  public items: IGamesPlayed[] = [];
  public itemsLength = [];
  public data = [];
  public queryParams: QueryParamsTable = {
    skip: 0,
    take: 10,
  };
  public isWritting = false;
  public timeZone: string = '';

  constructor(
    private readonly apiService: ApiService,
    private readonly router: Router,
    private readonly paidSupplierService: PaidSupplierService
  ) {
    this.pagination = this.defaultPaginationData;
  }

  async ngOnInit(): Promise<void> {
    await this.getoperatorData();

    this.debounceSearch
      .pipe(
        debounceTime(1000),
        tap(() => (this.isWritting = false)),
        distinctUntilChanged()
      )
      .subscribe((result) => {
        console.log({ result });
        this.resetValues();
        this.queryParams.globalFilter = result.trim();
        this.getAllTransactions();
      });
    this.getAllTransactions();
  }
  getAllTransactions() {
    this.apiService
      .list(`${ApiCore.OPERATOR}/get-last-games-played`, this.queryParams)
      .subscribe({
        next: (res) => {
          console.log('data from response', res);
          const pagination: Pagination = res.data.pagination;
          this.pagination = pagination;
          this.items = res.data.list.map((item: any) => {
            item.createdAt = this.parseDateWithOffset(
              item.createdAt,
              this.timeZone
            );
            return item;
          });
          const length = res.data.list.length;
          this.showing = length;
          this.total = pagination.totalItems;
          this.from = (pagination.currentPage - 1) * this.queryParams.take + 1;
          this.to = this.from + length - 1;
          this.setPages();
        },
        error: (err) => {
          console.log('Something went wrong');
        },
        complete: () => {
          this.loading = false;
        },
      });
  }
  searchInTable(e: string) {
    this.isWritting = true;
    this.loading = true;
    this.debounceSearch.next(e);
  }
  goToPage(e: number | string) {
    if (isNaN(e as number)) return;
    const page = e as number;
    console.log('GO TO ', page);
    this.queryParams.skip = (page - 1) * this.queryParams.take;
    this.getData();
  }
  getData() {
    this.getAllTransactions();
  }

  nextPage(): void {
    if (!this.pagination?.hasNextPage) return;
    const next = this.queryParams.skip;
    const jumpTo = next + this.queryParams.take;
    this.queryParams.skip = jumpTo;
    this.getData();
    console.log('next page skip to', jumpTo);
  }
  previousPage(): void {
    if (!this.pagination?.hasPreviousPage) return;
    const skip = this.queryParams.skip;
    const jumpTo = skip - this.queryParams.take;
    this.queryParams.skip = jumpTo;
    this.getData();
    console.log('next page skip to', jumpTo);
  }

  setPages() {
    const { currentPage, totalPages } = this.pagination;
    if (totalPages <= 4) {
      this.pages = Array.from({ length: totalPages }, (_, i) => i + 1);
      return;
    }
    const arrayPages: (number | string)[] = [];
    arrayPages.push(totalPages - totalPages + 1);
    arrayPages.push(totalPages - totalPages + 2);
    arrayPages.push('...');
    arrayPages.push(totalPages - 1);
    arrayPages.push(totalPages);

    arrayPages.splice(
      2,
      1,
      arrayPages.includes(currentPage) ? '...' : currentPage
    );
    this.pages = arrayPages;
  }
  resetValues() {
    this.queryParams.skip = 0;
    this.pagination = this.defaultPaginationData;
  }

  openGame(game: Game) {
    this.paidSupplierService.checkBeforeLaunch(game);
  }

  validateGameSession(game: Game) {
    this.apiService.getGameSession(ApiCore.SESSION_GAMES).subscribe((res) => {
      if (res.data.length === 0) {
        if (game.id) {
          this.router.navigate(['/app/home/open-game', game.id]);
        } else {
          this.router.navigate(['/app/home/open-game', game]);
        }
      } else {
        console.log('tiene un juego abierto');
      }
    });
  }

  async getoperatorData() {
    try {
      const responseOperator = await lastValueFrom(
        this.apiService.list(`${ApiCore.OPERATOR}/operator-config`)
      );
      this.timeZone = responseOperator.data.timeZone ?? '';
    } catch (error) {
      console.error('Error al obtener la configuración UTC:', error);
    }
  }

  parseDateWithOffset(date: string, timeZone: string) {
    const match = timeZone.match(/^UTC([+\-±])(\d{2}):(\d{2})$/);
    if (!match) return date;

    const sign = match[1] === '+' ? 1 : -1;
    const offsetHours = parseInt(match[2], 10);
    const offsetMinutes = parseInt(match[3], 10);

    const utcDate = new Date(date);

    const offsetMs = sign * ((offsetHours * 60 + offsetMinutes) * 60 * 1000);
    const localDate = new Date(utcDate.getTime() + offsetMs);

    return localDate.toLocaleString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  }
}
